/**
 * Extracts the most specific human-readable message from a backend error.
 *
 * Order of preference:
 *   1. Sterling validation errors at `details.sterlingError.errors` (an object
 *      keyed by field name with arrays of strings). All messages are joined
 *      with ". " so multi-field errors aren't truncated.
 *   2. `details.sterlingError.message` if Sterling returned a top-level string.
 *   3. `data.message` from the gateway envelope (the human-readable summary).
 *   4. `err.message` from the underlying error (often a generic axios string).
 *   5. The provided fallback.
 */
export function extractApiErrorMessage(err: any, fallback?: string): string {
  const data = err?.response?.data ?? err?.data ?? err;

  const sterlingError = data?.details?.sterlingError;
  if (sterlingError && typeof sterlingError === 'object') {
    const errors = sterlingError.errors;
    if (errors && typeof errors === 'object') {
      const messages: string[] = [];
      for (const key of Object.keys(errors)) {
        const value = errors[key];
        if (Array.isArray(value)) {
          for (const m of value) if (typeof m === 'string') messages.push(m);
        } else if (typeof value === 'string') {
          messages.push(value);
        }
      }
      if (messages.length > 0) return messages.join('. ');
    }
    if (typeof sterlingError.message === 'string' && sterlingError.message) {
      return sterlingError.message;
    }
  }

  if (data && typeof data.message === 'string' && data.message) {
    return data.message;
  }

  if (err?.message && typeof err.message === 'string') {
    return err.message;
  }

  return fallback || 'Something went wrong. Please try again.';
}
